import {
  ArrowRight,
  Calendar,
  FileText,
  Mail,
  ShieldCheck,
  Sparkles,
  Users,
  Workflow,
} from "lucide-react"
import { Link } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { FaqItem } from "@/components/marketing/faq-item"
import { FeatureCard } from "@/components/marketing/feature-card"
import { SocialProof } from "@/components/marketing/social-proof"
import { Step } from "@/components/marketing/step"

export function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <Link
            to="/"
            className="flex items-center gap-2 text-lg font-semibold tracking-tight"
          >
            <span className="grid size-7 place-items-center rounded-md bg-primary text-primary-foreground shadow-sm">
              <Sparkles className="size-4" aria-hidden="true" />
            </span>
            Hirefy
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost">Giriş yap</Button>
            </Link>
            <Link to="/signup">
              <Button>
                Ücretsiz dene <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden brand-gradient">
        <div className="container mx-auto px-6 py-24 text-center md:py-32">
          <Badge variant="secondary" className="mb-6">
            <Sparkles className="mr-1 size-3" aria-hidden="true" />
            AI destekli işe alım platformu
          </Badge>
          <h1 className="mx-auto max-w-3xl text-balance text-4xl font-bold tracking-tight md:text-6xl">
            Doğru adayı bulmak bu kadar{" "}
            <span className="brand-text-gradient">kolay</span> olmamıştı
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            Hirefy AI, CV'leri saniyeler içinde analiz eder; aday havuzunuzu
            yönetir, sürükle-bırak pipeline kurar ve ekip işbirliğini
            kolaylaştırır.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button size="lg" asChild>
              <Link to="/signup">
                Ücretsiz başla
                <ArrowRight className="ml-2 size-4" aria-hidden="true" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="#features">Özellikler</Link>
            </Button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            14 gün ücretsiz deneme · Kredi kartı gerekmez
          </p>
        </div>
      </section>

      <SocialProof />

      {/* FEATURES */}
      <section id="features" className="border-t">
        <div className="container mx-auto px-6 py-20">
          <div className="mb-12 text-center">
            <Badge variant="outline" className="mb-3">
              Özellikler
            </Badge>
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              İşe alımın her adımı tek platformda
            </h2>
            <p className="mt-3 text-lg text-muted-foreground">
              Dağınık tablolar ve onlarca tool yerine tek bir akış.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <FeatureCard
              icon={Sparkles}
              title="AI ile CV analizi"
              description="PDF CV'yi yüklediğinde Hirefy AI yapılandırılmış veri çıkarır, ilan ile eşleşme skoru üretir, güçlü/zayıf yönleri ve risk faktörlerini ortaya koyar."
            />
            <FeatureCard
              icon={Workflow}
              title="Pipeline & Kanban"
              description="Sürükle-bırak kanban, özelleştirilebilir aşamalar, gerçek zamanlı senkron. Farklı ilanlara farklı akışlar tanımla."
            />
            <FeatureCard
              icon={Users}
              title="Aday havuzu"
              description="Gelen başvuruları etiketle, filtrele, not al. Tüm ekip aynı bilgiyi görür."
            />
            <FeatureCard
              icon={Mail}
              title="E-posta & Inbox"
              description="Şablonlarla hızlı yanıt, threaded inbox, reply-to ile gelen mesajlar otomatik thread'e düşer."
            />
            <FeatureCard
              icon={Calendar}
              title="Mülakat planlama"
              description="Telefon, video ya da yüz yüze görüşme planla; AI üretilen mülakat soruları ile hazırlık yap, Google Calendar'a yaz."
            />
            <FeatureCard
              icon={FileText}
              title="Public kariyer sayfası"
              description="Yayınladığın ilanlar otomatik olarak /careers/<şirket> adresinde listelenir. PDF CV upload ile başvuru al."
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t bg-muted/30">
        <div className="container mx-auto px-6 py-20">
          <div className="mb-12 text-center">
            <Badge variant="outline" className="mb-3">
              Nasıl çalışır
            </Badge>
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              İşe alımı hızlandır
            </h2>
          </div>

          <div className="mx-auto max-w-4xl">
            <ol className="relative space-y-10 border-l border-primary/20 pl-8">
              <Step
                n={1}
                title="İlan oluştur & yayınla"
                body="AI ile başlığa göre tanım + gereksinim üret; dakikalar içinde pozisyonu yayına al. Kariyer sayfan otomatik güncellenir."
              />
              <Step
                n={2}
                title="Başvuruları topla"
                body="Adaylar public sayfadan CV yükleyerek başvurur. Her başvuru otomatik aday havuzuna düşer."
              />
              <Step
                n={3}
                title="AI ile değerlendir"
                body="Tek tıkla — ya da toplu — analiz başlat; skor, seviye, beceriler, risk faktörleri ve mülakat soruları saniyeler içinde gelir."
              />
              <Step
                n={4}
                title="Pipeline'da ilerlet"
                body="Sürükle-bırakla aşama geçir; ekibin her adımı ve yazışmayı görür. Mülakat, teklif, işe alım — hepsi aynı yerde."
              />
            </ol>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="border-t">
        <div className="container mx-auto px-6 py-16">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary-soft text-primary">
              <ShieldCheck className="size-5" aria-hidden="true" />
            </div>
            <h2 className="mt-4 text-2xl font-bold tracking-tight">
              Veriniz sizde, erişim ekibinizde
            </h2>
            <p className="mt-3 text-muted-foreground">
              Her şirket kendi izole veri kapsamına sahip; başvurular,
              aktiviteler ve CV'ler organizasyon sınırı dışına çıkmaz. AI
              analizleri yalnızca analiz anında sağlayıcıya gönderilir,
              eğitim için saklanmaz. KVKK uyumlu yapılandırma şablonu hazır.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t bg-muted/30">
        <div className="container mx-auto px-6 py-20">
          <div className="mb-10 text-center">
            <Badge variant="outline" className="mb-3">
              SSS
            </Badge>
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              Sık sorulanlar
            </h2>
          </div>

          <div className="mx-auto max-w-3xl divide-y rounded-2xl border bg-card p-2 sm:p-4">
            <FaqItem
              q="Hirefy ücretsiz mi?"
              a="Küçük ekipler için bir ücretsiz plan bulunuyor. Daha yüksek kota ve gelişmiş özellikler için ücretli planlar yakında aktif olacak."
            />
            <FaqItem
              q="Kendi domain'imizi kullanabilir miyiz?"
              a="Özel subdomain desteği (careers.sirketin.com) Business planda olacak. Şu an <code>/careers/&lt;şirket-slug&gt;</code> üzerinden erişilebiliyor."
            />
            <FaqItem
              q="AI analiz nasıl çalışıyor?"
              a="PDF CV + iş ilanı Hirefy AI'a gönderilir; 0-100 skor, seviye, güçlü/zayıf yönler, risk faktörleri, beceri listesi ve isteğe bağlı mülakat soruları JSON olarak döner."
            />
            <FaqItem
              q="KVKK/GDPR uyumu var mı?"
              a="Org-scope sorgu izolasyonu, imzalı URL ile CV erişimi, audit log (activity_log) sağlanmıştır. Prod kurulumda DPA ve KVKK aydınlatma metni gereklidir; şablonumuz legal sayfalarında."
            />
            <FaqItem
              q="Ekip üyelerimi nasıl eklerim?"
              a="Settings → Ekip sekmesinden e-posta ile davet gönderebilirsiniz. Roller: Sahip, Yönetici, İşe alım uzmanı, Gözlemci."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t brand-gradient">
        <div className="container mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
            Ekibinizi kurmaya başlayın
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            Dakikalar içinde hesap oluşturun, ilk ilanınızı yayına alın.
          </p>
          <div className="mt-8">
            <Button size="lg" asChild>
              <Link to="/signup">
                Ücretsiz başla
                <ArrowRight className="ml-2 size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t bg-muted/30">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-6 py-6 text-sm text-muted-foreground sm:flex-row">
          <div>© {new Date().getFullYear()} Hirefy</div>
          <div className="flex gap-4">
            <Link to="/legal/terms" className="hover:underline">
              Şartlar
            </Link>
            <Link to="/legal/privacy" className="hover:underline">
              Gizlilik
            </Link>
            <Link to="/legal/kvkk" className="hover:underline">
              KVKK
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
